import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Scale, MessageSquare, FileText, LogOut, Plus, Trash2, Gavel, ShieldAlert, MapPin, Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n/LanguageContext";
import AvocatEnOrLogo from "./AvocatEnOrLogo";
import { ScrollArea } from "./ui/scroll-area";
import api from "../lib/api";
import { toast } from "sonner";

export default function Sidebar({ conversations = [], onNewChat, onSelectConv, activeConvId, onDeleteConv }) {
  const { user, logout, setUser } = useAuth();
  const { t } = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const [regions, setRegions] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/regions").then((r) => setRegions(r.data)).catch(() => {});
  }, []);

  // Close sidebar when route changes (so navigation feels fluid on mobile)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleRegionChange = async (e) => {
    const newRegion = e.target.value;
    try {
      const res = await api.patch("/auth/me", { region: newRegion });
      setUser((u) => ({ ...u, region: res.data.region }));
      toast.success(`Région : ${regions.find((r) => r.id === newRegion)?.label}`);
    } catch {
      toast.error("Erreur");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const closeOnAction = (cb) => () => {
    setMobileOpen(false);
    if (typeof cb === "function") cb();
  };

  return (
    <>
      {/* Hamburger button — mobile only */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-30 h-10 w-10 rounded-sm bg-slate-900 text-white flex items-center justify-center shadow-lg"
        data-testid="sidebar-toggle"
        aria-label="Ouvrir le menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Overlay (mobile only) */}
      {mobileOpen && (
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm"
          data-testid="sidebar-overlay"
          aria-label="Fermer le menu"
        />
      )}

      <aside
        className={`bg-slate-900 text-white flex flex-col w-72 flex-shrink-0 transition-transform duration-200 ease-out
          md:sticky md:top-0 md:h-screen md:translate-x-0
          fixed inset-y-0 left-0 h-screen z-50
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <Link to="/sos" className="flex items-center gap-2" data-testid="sidebar-logo" onClick={() => setMobileOpen(false)}>
            <AvocatEnOrLogo size={28} variant="full" background="black" className="rounded-sm" />
            <span className="font-serif-display text-lg font-semibold tracking-tight">Un Avocat en Or</span>
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="md:hidden h-8 w-8 rounded-sm hover:bg-slate-800 flex items-center justify-center text-slate-400"
            aria-label="Fermer le menu"
            data-testid="sidebar-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-3 space-y-1">
          {/* GROS BOUTON ACCUEIL SOS — en premier, rouge pour signaler l'urgence */}
          <Link to="/sos">
            <Button
              className={`w-full justify-start rounded-sm h-11 bg-red-600 hover:bg-red-700 text-white font-medium ${
                location.pathname === "/sos" ? "ring-2 ring-red-300" : ""
              }`}
              data-testid="nav-sos"
            >
              <ShieldAlert className="h-4 w-4 mr-2" /> {t("nav_sos_home")}
            </Button>
          </Link>
          <Button
            onClick={closeOnAction(onNewChat)}
            variant="ghost"
            className="w-full justify-start rounded-sm h-10 text-slate-300 hover:bg-slate-800 hover:text-white"
            data-testid="new-chat-button"
          >
            <Plus className="h-4 w-4 mr-2" /> {t("nav_new_chat")}
          </Button>
          <Link to="/dashboard">
            <Button
              variant="ghost"
              className={`w-full justify-start rounded-sm h-10 text-slate-300 hover:bg-slate-800 hover:text-white ${
                location.pathname === "/dashboard" ? "bg-slate-800 text-white" : ""
              }`}
              data-testid="nav-chat"
            >
              <MessageSquare className="h-4 w-4 mr-2" /> {t("nav_my_consultations")}
            </Button>
          </Link>
          <Link to="/documents">
            <Button
              variant="ghost"
              className={`w-full justify-start rounded-sm h-10 text-slate-300 hover:bg-slate-800 hover:text-white ${
                location.pathname === "/documents" ? "bg-slate-800 text-white" : ""
              }`}
              data-testid="nav-documents"
            >
              <FileText className="h-4 w-4 mr-2" /> {t("nav_documents")}
            </Button>
          </Link>
          <Link to="/analyses">
            <Button
              variant="ghost"
              className={`w-full justify-start rounded-sm h-10 text-slate-300 hover:bg-slate-800 hover:text-white ${
                location.pathname === "/analyses" ? "bg-slate-800 text-white" : ""
              }`}
              data-testid="nav-analyses"
            >
              <Gavel className="h-4 w-4 mr-2" /> {t("nav_who_is_right")}
            </Button>
          </Link>
        </div>

        <div className="px-5 pt-4 pb-2">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("nav_history")}</p>
        </div>
        <ScrollArea className="flex-1 px-3">
          <div className="space-y-1 pb-4">
            {conversations.length === 0 && (
              <p className="text-xs text-slate-500 px-3 py-2" data-testid="no-conversations">{t("nav_no_consultation")}</p>
            )}
            {conversations.map((c) => (
              <div
                key={c.conversation_id}
                className={`group flex items-start gap-2 px-3 py-2 rounded-sm cursor-pointer transition-colors ${
                  activeConvId === c.conversation_id ? "bg-slate-800" : "hover:bg-slate-800/50"
                }`}
                onClick={closeOnAction(() => onSelectConv && onSelectConv(c.conversation_id))}
                data-testid={`conversation-item-${c.conversation_id}`}
              >
                <MessageSquare className="h-3.5 w-3.5 mt-1 flex-shrink-0 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-200 line-clamp-2 leading-snug">{c.title}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteConv && onDeleteConv(c.conversation_id); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-red-400"
                  data-testid={`delete-conv-${c.conversation_id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-sm object-cover" />
            ) : (
              <div className="h-8 w-8 rounded-sm bg-slate-700 flex items-center justify-center text-xs font-semibold">
                {user?.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>

          {regions.length > 0 && (
            <div className="mb-3">
              <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                <MapPin className="h-3 w-3" /> Région
              </label>
              <select
                value={user?.region || "all"}
                onChange={handleRegionChange}
                className="w-full bg-slate-800 border border-slate-700 text-white text-xs rounded-sm h-8 px-2 focus:outline-none focus:border-[#B89C65]"
                data-testid="region-selector"
              >
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </div>
          )}

          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start rounded-sm text-slate-400 hover:bg-slate-800 hover:text-white h-9 text-xs"
            data-testid="logout-button"
          >
            <LogOut className="h-3.5 w-3.5 mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>
    </>
  );
}
